/**
 * Telegram 消息内控上报的**装配与发送**。
 *
 * ## 链路
 *
 *   rootScope 'message_receive' / 'message_sent'（挂钩见 utils/pcApi.js）
 *     ↓ reportTelegramMessage(message)
 *   本模块：归一化（见 chatReportNormalize.ts）+ 攒批
 *     ↓ window.dmexBridge.reportChat({ sysId, sysType, messages })
 *   Dmex preload → 主进程 → 渲染层补 memberId → POST /api/v1/client/risk/chat-messages/batch
 *
 * 载荷形状与 WhatsApp 那条路**完全一致**（两边共用客户端渲染层的同一份处理逻辑），
 * 见 dmex 的 preload/tg.js 与 api/chat-report.ts。
 *
 * ## 两条硬约束
 *
 * 1. **异常绝不外溢**。挂钩的那两个事件被 AI 回复、翻译、粉丝同步共用，
 *    这里抛出去会连带弄坏那三个功能。所以对外的入口一律吞掉异常、只记日志。
 *
 * 2. **本期没有持久化队列**。攒批只在内存里：页面刷新、或上报失败即丢弃。
 *    WhatsApp 那条路有 IndexedDB 队列 + 退避重试（`modules/ws/src/chat-outbox.js`），
 *    要同等可靠就得把它搬过来 —— 那是下一步。失败会打 warn，便于判断丢了多少。
 *
 * 唯一的例外是「账号还没绑定工作组」：客户端会把这些条目原样交回
 * （`notBoundKeys`），它们留在队列里等绑定完成 —— 那是**临时**状态，丢掉可惜。
 */
import rootScope from '../../lib/rootScope';

import {
  normalizeTelegramMessage,
  SYS_TYPE_TELEGRAM,
  type TelegramChatReportItem
} from './chatReportNormalize';

/** 文档：批量上报单次最多 100 条 */
const BATCH_MAX = 100;

/** 攒批窗口。消息往往成串到达，攒一下能把几十条合成一次请求 */
const FLUSH_DELAY_MS = 2000;

/** 队列上限。没有持久化，所以必须有个天花板，不能让它无限涨 */
const PENDING_MAX = 1000;

interface DmexBridge {
  reportChat: (payload: unknown) => Promise<any>;
}

function getBridge(): DmexBridge | null {
  const bridge = (globalThis as {dmexBridge?: DmexBridge}).dmexBridge;
  return bridge && typeof bridge.reportChat === 'function' ? bridge : null;
}

/** 待发队列。按幂等键去重 —— 同一条消息可能被多个事件带到这里 */
const pending = new Map<string, TelegramChatReportItem>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
  if(flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    // flush 是 async 的，这里必须接住，否则是一个未处理的 rejection
    flush().catch((error) => console.warn('[chat-report] flush 异常', error));
  }, FLUSH_DELAY_MS);
}

async function flush(): Promise<void> {
  flushTimer = null;
  if(pending.size === 0) return;

  const bridge = getBridge();
  if(!bridge) {
    // 不在 dmex 客户端里跑（例如直接用浏览器打开 tweb）：清掉，别无限堆积
    pending.clear();
    return;
  }

  // 工作号 id。登录后才有，所以拿不到时留在队列里等下一轮 —— 这是临时状态
  const sysId = rootScope.myId ? String(rootScope.myId) : '';
  if(!sysId) {
    scheduleFlush();
    return;
  }

  // 一次最多 100 条（文档限制）。取出的这批先从队列移除，避免下一轮重复发
  const batch = [...pending.values()].slice(0, BATCH_MAX);
  for(const item of batch) pending.delete(item.idempotencyKey);

  try {
    const result = await bridge.reportChat({
      sysId,
      sysType: SYS_TYPE_TELEGRAM,
      messages: batch
    });

    if(!result?.ok) {
      // 本期不重试（无持久化队列），只记账便于判断丢了多少
      console.warn('[chat-report] 上报失败，本批丢弃', {count: batch.length, result});
    } else if(Array.isArray(result.notBoundKeys) && result.notBoundKeys.length > 0) {
      // 账号还没绑定工作组，客户端把这些条目交回来了。放回队列等绑定完成 ——
      // 这是唯一值得保留的失败，因为条件一恢复就能发出去
      const notBound = new Set<string>(result.notBoundKeys);
      for(const item of batch) {
        if(notBound.has(item.idempotencyKey)) pending.set(item.idempotencyKey, item);
      }
      console.log('[chat-report] 账号未绑定工作组，相关条目留在队列', {count: notBound.size});
    }
  } catch(error) {
    console.warn('[chat-report] 上报异常，本批丢弃', {count: batch.length, error});
  }

  // 队列里还有（超出单批上限、或被交回的未绑定条目）就继续
  if(pending.size > 0) scheduleFlush();
}

/**
 * 入队一条消息。
 *
 * **吞掉所有异常**：调用点是 AI 回复/翻译/粉丝同步共用的事件处理，
 * 这里抛出去会连带弄坏那些功能。
 */
export function reportTelegramMessage(message: unknown): void {
  try {
    // 没有桥就什么都不做：归一化白做一遍没意义，而且这条路在浏览器里也会走到
    if(!getBridge()) return;

    const item = normalizeTelegramMessage(message);
    if(!item) return;

    // 满了就丢最旧的：没有持久化，留着最新的比留着最旧的有用
    if(pending.size >= PENDING_MAX && !pending.has(item.idempotencyKey)) {
      const oldest = pending.keys().next().value;
      if(oldest !== undefined) pending.delete(oldest);
      console.warn('[chat-report] 队列已满，丢弃最旧条目', {max: PENDING_MAX});
    }

    pending.set(item.idempotencyKey, item);
    scheduleFlush();
  } catch(error) {
    console.warn('[chat-report] 入队失败，已跳过该条', error);
  }
}
